// React auth hook — login/logout/role detection with SWA built-in auth or dev mode
import { useState, useEffect, useCallback } from "react";
import { fetchClientPrincipal, login, logout, getActiveUser, isDevMode } from "@/services/auth";
import type { AuthUser, UserRole } from "@/types";

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDevMode: boolean;
  login: (role?: UserRole, redirectTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function init() {
      try {
        if (!isDevMode()) {
          await fetchClientPrincipal();
        }
        setUser(getActiveUser());
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }
    init();
  }, []);

  const handleLogin = useCallback(async (role?: UserRole, redirectTo?: string) => {
    try {
      await login(role, redirectTo);
      setUser(getActiveUser());
    } catch {
      // User cancelled or error
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      setUser(null);
    } catch {
      // Logout error
    }
  }, []);

  const hasRole = useCallback(
    (role: UserRole) => user?.role === role,
    [user]
  );

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    isDevMode: isDevMode(),
    login: handleLogin,
    logout: handleLogout,
    hasRole,
  };
}
